import clsx from 'clsx'
import styles from './MemberName.module.css'

interface MemberNameProps {
  /** The member's shown name (`display_name`); absent/null → the handle, exactly as before. */
  name?: string | null
  handle: string
  /** DealHunt's display form of the handle (`handle_display`), when the shape carries one. */
  handleDisplay?: string | null
  /** The name alone when there is one (the feed row's bylines, 2026-10-19): the handle stays the
   *  key — the hover card and the member page still print it. Without a name, the handle as ever. */
  nameOnly?: boolean
  className?: string
}

/**
 * How a member is named everywhere (identity ticket §2.4: "`Name @handle` always render
 * together"): the shown name, then `@handle` small and muted on the same line — the handle keeps
 * its role as the URL key and the mention token. Without a name it is the bare handle text, the
 * markup every author line had before (no wrapper, no `@`). Phrasing content only: it sits inside
 * links, `<b>`, `<h1>` and author `<p>`s.
 *
 * `nameOnly` (the community feed row, Guillaume 2026-10-19): a shown name prints alone — on a
 * phone the one-line byline had no room for `Name @handle · Owner · … ↻ Would buy again · 4h`
 * (Directive d655303b: revisit the identity rule before cutting a pill). A pseudonym member
 * (`show_identity` off, or a review from before the mandatory sign-in) still prints the handle.
 */
const MemberName: React.VFC<MemberNameProps> = ({
  name,
  handle,
  handleDisplay,
  nameOnly = false,
  className,
}) => {
  if (!name) {
    // A bare text node (no wrapper element) so the no-name markup is byte-identical to before.
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{handle}</>
  }
  if (nameOnly) {
    return (
      <span className={clsx(styles.member, className)}>
        <span className={styles.name}>{name}</span>
      </span>
    )
  }
  return (
    <span className={clsx(styles.member, className)}>
      <span className={styles.name}>{name}</span>{' '}
      <span className={styles.handle}>{`@${handleDisplay || handle}`}</span>
    </span>
  )
}

export default MemberName
